/*!
 * Copyright 2024 - Swiss Data Science Center (SDSC)
 * A partnership between École Polytechnique Fédérale de Lausanne (EPFL) and
 * Eidgenössische Technische Hochschule Zürich (ETHZ).
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect } from "react";

import { DEFAULT_PERMISSIONS } from "../../permissionsV2/permissions.constants";
import type { PermissionsWithLoadingState } from "../../permissionsV2/permissions.types";
import { projectV2Api } from "../../projectsV2/api/projectV2.enhanced-api";

interface UseProjectPermissionsArgs {
  projectId: string;
}

export default function useProjectPermissions({
  projectId,
}: UseProjectPermissionsArgs): PermissionsWithLoadingState {
  const { currentData, isLoading, isError, isUninitialized, error } =
    projectV2Api.endpoints.getProjectsByProjectIdPermissions.useQueryState(
      projectId ? { projectId } : skipToken,
    );
  const [fetchPermissions] =
    projectV2Api.endpoints.getProjectsByProjectIdPermissions.useLazyQuery();

  useEffect(() => {
    if (projectId && isUninitialized) {
      fetchPermissions({ projectId });
    }
  }, [fetchPermissions, isUninitialized, projectId]);

  const isLoadingPermissions = isLoading || !!(projectId && isUninitialized);
  const arePermissionsResolved =
    !isLoadingPermissions && !isError && currentData != null;

  if (isLoading || isError || !currentData) {
    return {
      ...DEFAULT_PERMISSIONS,
      arePermissionsResolved: false,
      isLoadingPermissions,
      isPermissionsError: isError,
      permissionsError: isError ? error : undefined,
    };
  }

  return {
    ...DEFAULT_PERMISSIONS,
    ...currentData,
    arePermissionsResolved,
    isLoadingPermissions: false,
    isPermissionsError: false,
    permissionsError: undefined,
  };
}
